import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { toast } from "sonner";
import { X, Loader2 } from "lucide-react";

const WAIT_OPTIONS = [
  { category: "no_wait", minutes: 0, label: "No line — walk right up", color: "bg-emerald-500", textColor: "text-emerald-700", bgLight: "bg-emerald-50", border: "border-emerald-200" },
  { category: "under_5", minutes: 3, label: "Short — under 5 minutes", color: "bg-emerald-500", textColor: "text-emerald-700", bgLight: "bg-emerald-50", border: "border-emerald-200" },
  { category: "5_to_15", minutes: 10, label: "Moderate — 10 to 15 minutes", color: "bg-amber-500", textColor: "text-amber-700", bgLight: "bg-amber-50", border: "border-amber-200" },
  { category: "15_to_30", minutes: 25, label: "Long — 20 or more minutes", color: "bg-orange-500", textColor: "text-orange-700", bgLight: "bg-orange-50", border: "border-orange-200" },
  { category: "over_30", minutes: 35, label: "Selling out fast", color: "bg-red-500", textColor: "text-red-700", bgLight: "bg-red-50", border: "border-red-200" },
];

export default function WaitTimeSheet({ businessId, businessName, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [gettingGps, setGettingGps] = useState(false);
  const [coords, setCoords] = useState(null);

  useEffect(() => {
    if (navigator.geolocation) {
      setGettingGps(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          setGettingGps(false);
        },
        () => { setGettingGps(false); toast.error("Location access required to report wait time"); },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  }, []);

  const handleSelect = async (option) => {
    if (!coords) { toast.error("Waiting for GPS..."); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/businesses/wait-time`, {
        business_id: businessId,
        wait_minutes: option.minutes,
        wait_category: option.category,
        lat: coords.lat,
        lng: coords.lng,
      });
      if (navigator.vibrate) navigator.vibrate(50);
      toast.success("Thanks — your community appreciates it!");
      onSuccess?.(res.data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit wait time");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" data-testid="wait-time-sheet">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="relative w-full max-w-md bg-white rounded-t-2xl px-5 pt-4 pb-8" style={{ animation: "slideUp 0.3s ease-out" }}>
        <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-4" />

        <button onClick={onClose} className="absolute top-4 right-4 w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center" data-testid="wait-sheet-close">
          <X className="w-4 h-4 text-slate-400" />
        </button>

        <h2 className="text-base font-black text-slate-800 mb-1" data-testid="wait-sheet-title">How's the line?</h2>
        <p className="text-xs text-slate-400 mb-4">{businessName}</p>

        {gettingGps && (
          <div className="flex items-center gap-2 text-xs text-slate-400 mb-3">
            <Loader2 className="w-3 h-3 animate-spin" /> Getting your location...
          </div>
        )}

        <div className="space-y-2">
          {WAIT_OPTIONS.map((opt) => (
            <button
              key={opt.category}
              onClick={() => handleSelect(opt)}
              disabled={loading || !coords}
              className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 transition hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 ${opt.bgLight} ${opt.border}`}
              data-testid={`wait-option-${opt.category}`}
            >
              <div className={`w-3 h-3 rounded-full ${opt.color} shrink-0`} />
              <span className={`text-sm font-bold ${opt.textColor}`}>{opt.label}</span>
            </button>
          ))}
        </div>

        {loading && (
          <div className="flex items-center justify-center mt-3">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
      `}</style>
    </div>
  );
}
